import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import CRMNav from "./CRMNav";
import ProspeccionNuevoModal from "./ProspeccionNuevoModal";
import ProspeccionImportarModal from "./ProspeccionImportarModal";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

// ─── Constantes v2 ────────────────────────────────────────────────────────────
const ESTADOS_ACTIVOS   = ["sin_tocar", "esperando_respuesta", "en_dialogo"];
const ESTADOS_GUARDADOS = ["en_pausa", "frio"];
const TODOS_LOS_ESTADOS = [...ESTADOS_ACTIVOS, ...ESTADOS_GUARDADOS];

const ESTADO_LABEL = {
  sin_tocar:           "Sin tocar",
  esperando_respuesta: "Esperando respuesta",
  frio:                "Frío",
  en_dialogo:          "En diálogo",
  en_pausa:            "En pausa",
};

const ESTADO_COLOR = {
  sin_tocar:           "#6B7280",
  esperando_respuesta: "#a16207",
  en_dialogo:          "#1d4ed8",
  en_pausa:            "#374151",
  frio:                "#475569",
};

const ESTADO_BG_TARJETA = {
  sin_tocar:           "#fff",
  esperando_respuesta: "#fffbeb",
  en_dialogo:          "#eff6ff",
  en_pausa:            "#fff",
  frio:                "#fff",
};

const UMBRAL_DIAS_DEFAULT = 2;

// ─── Tarjeta ──────────────────────────────────────────────────────────────────
const ProspectoCard = ({ prospecto, onClick, umbral }) => {
  const bg = ESTADO_BG_TARJETA[prospecto.estado] || "#fff";
  const alerta = prospecto.estado === "esperando_respuesta"
                 && prospecto.dias_esperando != null
                 && prospecto.dias_esperando > umbral;

  return (
    <div
      onClick={onClick}
      style={{
        background: bg,
        border: `1.5px solid ${alerta ? "#fbbf24" : "#f0e6ec"}`,
        borderRadius: 10,
        padding: "12px 14px",
        marginBottom: 10,
        cursor: "pointer",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        transition: "all 0.15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9f0059"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = alerta ? "#fbbf24" : "#f0e6ec"; e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, color: "#121212", marginBottom: 4 }}>
        {prospecto.empresa}
      </div>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>
        {prospecto.segmento && <span style={{ marginRight: 8 }}>{prospecto.segmento}</span>}
        {prospecto.origen && <span>· {prospecto.origen}</span>}
      </div>
      {prospecto.ubicacion && (
        <div style={{ fontSize: 11, color: "#aaa", marginBottom: 6 }}>📍 {prospecto.ubicacion}</div>
      )}

      {/* Info contextual según estado */}
      {prospecto.estado === "esperando_respuesta" && (
        <div style={{ fontSize: 11, fontWeight: 600, color: alerta ? "#a16207" : "#6b7280" }}>
          {prospecto.toques_total} toque{prospecto.toques_total !== 1 ? "s" : ""}
          {prospecto.dias_esperando != null && (
            <> · esperando hace {prospecto.dias_esperando}d {alerta && "⚠"}</>
          )}
        </div>
      )}
      {prospecto.estado === "en_dialogo" && prospecto.ultimo_hito && (
        <div style={{ fontSize: 11, color: "#1d4ed8", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          ✱ {prospecto.ultimo_hito}
        </div>
      )}
      {prospecto.estado === "en_pausa" && prospecto.fecha_recordatorio && (
        <div style={{ fontSize: 11, color: "#6b7280" }}>
          🔔 Retomar el {new Date(prospecto.fecha_recordatorio).toLocaleDateString("es-AR")}
        </div>
      )}
    </div>
  );
};

// ─── Columna ──────────────────────────────────────────────────────────────────
const Columna = ({ estado, prospectos, umbral, onCardClick }) => {
  const color = ESTADO_COLOR[estado];
  return (
    <div style={{
      flex: 1, minWidth: 240, background: "#f9f5f7",
      border: "1.5px solid #f0e6ec", borderRadius: 10,
      padding: 14, display: "flex", flexDirection: "column",
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 14, paddingBottom: 8, borderBottom: `2px solid ${color}`,
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {ESTADO_LABEL[estado]}
        </span>
        <span style={{
          background: "#fff", color,
          padding: "2px 9px", borderRadius: 20,
          fontSize: 11, fontWeight: 700,
        }}>
          {prospectos.length}
        </span>
      </div>
      {prospectos.length === 0 ? (
        <p style={{ color: "#bbb", fontSize: 12, textAlign: "center", marginTop: 20 }}>— vacío —</p>
      ) : (
        prospectos.map((p) => (
          <ProspectoCard key={p.id} prospecto={p} umbral={umbral} onClick={() => onCardClick(p.id)} />
        ))
      )}
    </div>
  );
};

// ─── KPI ──────────────────────────────────────────────────────────────────────
const Kpi = ({ label, value, color = "#121212" }) => (
  <div style={{
    flex: 1, background: "#fff", border: "1.5px solid #f0e6ec",
    borderRadius: 10, padding: "12px 16px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
  }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
  </div>
);

// ─── Modal lista de pausa/frío ────────────────────────────────────────────────
const ListaModal = ({ titulo, prospectos, onClose, onCardClick, umbral }) => (
  <div onClick={onClose} style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20,
  }}>
    <div onClick={(e) => e.stopPropagation()} style={{
      background: "#fff", borderRadius: 14, padding: 24,
      maxWidth: 560, width: "100%", maxHeight: "85vh", overflowY: "auto",
      boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
    }}>
      <h2 style={{ margin: "0 0 16px", fontSize: 18, color: "#9f0059" }}>{titulo} ({prospectos.length})</h2>
      {prospectos.length === 0 ? (
        <p style={{ color: "#aaa" }}>— vacío —</p>
      ) : (
        prospectos.map((p) => (
          <ProspectoCard key={p.id} prospecto={p} umbral={umbral} onClick={() => onCardClick(p.id)} />
        ))
      )}
    </div>
  </div>
);

// ─── Componente principal ────────────────────────────────────────────────────
const ProspeccionEmbudo = () => {
  const history = useHistory();
  const [porEstado, setPorEstado] = useState(Object.fromEntries(TODOS_LOS_ESTADOS.map((e) => [e, []])));
  const [conteos, setConteos]     = useState({});
  const [loading, setLoading]     = useState(true);
  const [showNew, setShowNew]     = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showGuardado, setShowGuardado] = useState(null); // 'en_pausa' | 'frio' | null

  // KPIs
  const [kpis, setKpis] = useState({ nuevos_esta_semana: 0, toques_esta_semana: 0, respuestas_esta_semana: 0, esperando_mas_de_n_dias: 0, umbral_dias: UMBRAL_DIAS_DEFAULT });

  // Filtros
  const [fSegmento, setFSegmento] = useState("");
  const [fOrigen,   setFOrigen]   = useState("");

  // Catálogo de segmentos
  const [segmentos, setSegmentos] = useState([]);
  useEffect(() => {
    axios.get(`${BASE}/buscar_segmentos.php`)
      .then((r) => { if (Array.isArray(r.data)) setSegmentos(r.data); })
      .catch(() => {});
  }, []);

  const fetchProspectos = useCallback(() => {
    setLoading(true);
    const fd = new FormData();
    if (fSegmento) fd.append("segmento", fSegmento);
    if (fOrigen)   fd.append("origen", fOrigen);

    axios.post(`${BASE}/prospectos_listar.php`, fd)
      .then((r) => {
        if (r.data?.success) {
          setPorEstado(r.data.por_estado);
          setConteos(r.data.conteos);
        } else {
          toast.error(r.data?.mensaje || "Error al cargar prospectos");
        }
      })
      .catch(() => toast.error("Error de conexión"))
      .finally(() => setLoading(false));
  }, [fSegmento, fOrigen]);

  const fetchKpis = useCallback(() => {
    axios.get(`${BASE}/prospectos_metricas_semana.php`)
      .then((r) => { if (r.data?.success) setKpis(r.data); })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchProspectos(); }, [fetchProspectos]);
  useEffect(() => { fetchKpis(); }, [fetchKpis]);

  return (
    <div style={{ minHeight: "100vh", background: "#f7f4f6", padding: "28px 20px", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
    <div style={{ maxWidth: 1300, margin: "0 auto" }}>
      <CRMNav />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#121212" }}>Prospección</h2>
          <p style={{ margin: "2px 0 0", color: "#888", fontSize: 13 }}>
            Embudo activo · {Object.values(conteos).reduce((a, b) => a + b, 0)} prospectos
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => history.push("/dashboard/crm/prospeccion/mi-semana")} style={btnSecondary}>📅 Mi semana</button>
          <button onClick={() => history.push("/dashboard/crm/prospeccion/descartados")} style={btnSecondary}>🗑 Descartados</button>
          <button onClick={() => setShowImport(true)} style={btnSecondary}>⬆ Importar CSV</button>
          <button onClick={() => setShowNew(true)} style={btnPrimary}>+ Nuevo prospecto</button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: 12, marginBottom: 18 }}>
        <Kpi label="Nuevos esta semana"     value={kpis.nuevos_esta_semana} />
        <Kpi label="Toques esta semana"     value={kpis.toques_esta_semana} />
        <Kpi label="Respuestas esta semana" value={kpis.respuestas_esta_semana} color="#1d4ed8" />
        <Kpi label={`Esperando hace +${kpis.umbral_dias || UMBRAL_DIAS_DEFAULT} días`} value={kpis.esperando_mas_de_n_dias} color="#a16207" />
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center" }}>
        <select value={fSegmento} onChange={(e) => setFSegmento(e.target.value)} style={selectStyle}>
          <option value="">Todos los segmentos</option>
          {segmentos.map((s) => <option key={s.id} value={s.nombre}>{s.nombre}</option>)}
        </select>
        <input type="text" placeholder="Filtrar por origen…" value={fOrigen}
          onChange={(e) => setFOrigen(e.target.value)} style={{ ...selectStyle, minWidth: 180 }} />
        {(fSegmento || fOrigen) && (
          <button onClick={() => { setFSegmento(""); setFOrigen(""); }}
            style={{ background: "none", border: "none", color: "#9f0059", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            ✕ Limpiar filtros
          </button>
        )}
      </div>

      {/* Embudo: 3 columnas activas */}
      {loading ? (
        <p style={{ textAlign: "center", color: "#aaa", padding: 40 }}>Cargando…</p>
      ) : (
        <>
          <div style={{ display: "flex", gap: 14 }}>
            {ESTADOS_ACTIVOS.map((estado) => (
              <Columna
                key={estado}
                estado={estado}
                prospectos={porEstado[estado] || []}
                umbral={kpis.umbral_dias || UMBRAL_DIAS_DEFAULT}
                onCardClick={(id) => history.push(`/dashboard/crm/prospeccion/detalle/${id}`)}
              />
            ))}
          </div>

          {/* Línea + botones guardados */}
          <div style={{ borderTop: "1.5px solid #e0d6dc", marginTop: 24, paddingTop: 18, display: "flex", gap: 12, justifyContent: "center" }}>
            {ESTADOS_GUARDADOS.map((estado) => (
              <button key={estado} onClick={() => setShowGuardado(estado)}
                style={{
                  padding: "10px 24px", background: "#fff",
                  border: "1.5px solid #d1d5db", borderRadius: 8,
                  fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#374151",
                }}>
                {ESTADO_LABEL[estado]} ({conteos[estado] || 0})
              </button>
            ))}
          </div>
        </>
      )}

      {/* Modales */}
      {showNew && (
        <ProspeccionNuevoModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); fetchProspectos(); fetchKpis(); }} />
      )}
      {showImport && (
        <ProspeccionImportarModal onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); fetchProspectos(); fetchKpis(); }} />
      )}
      {showGuardado && (
        <ListaModal
          titulo={ESTADO_LABEL[showGuardado]}
          prospectos={porEstado[showGuardado] || []}
          umbral={kpis.umbral_dias || UMBRAL_DIAS_DEFAULT}
          onCardClick={(id) => history.push(`/dashboard/crm/prospeccion/detalle/${id}`)}
          onClose={() => setShowGuardado(null)}
        />
      )}
    </div>
    </div>
  );
};

const selectStyle = { padding: "8px 12px", border: "1.5px solid #e0d6dc", borderRadius: 7, fontSize: 13, background: "#fff", color: "#222", outline: "none", cursor: "pointer" };
const btnPrimary   = { padding: "9px 22px", background: "#9f0059", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" };
const btnSecondary = { padding: "9px 18px", background: "#fff", color: "#9f0059", border: "1.5px solid #9f0059", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" };

export default ProspeccionEmbudo;
